/* ===========================================================================
 * CLOCK TIME AS `HH:MM`, 24-HOUR, AND NOTHING ELSE.
 *
 * NO AM/PM, and that is a product decision rather than a stylistic one. A shift
 * boundary, a roster row and a punch are read side by side against each other
 * and against a report; `07:14` sorts, aligns and subtracts, and `7:14 AM` does
 * none of the three. Every stored time in this product is 24-hour, so a control
 * that offered a meridiem toggle would be inventing a second way to say a value
 * that has one — and would put the app's one ambiguous hour (12) in a control
 * a site operator uses at speed.
 *
 * A TIME IS NOT AN INSTANT. Same rule as `dayKey.ts`: this is a wall-clock
 * reading with no date and no zone attached, so nothing here constructs a
 * `Date` at all. There is nothing to shift.
 * ======================================================================== */

/** A wall-clock time as `HH:MM`, 24-hour, zero-padded. */
export type TimeValue = string;

const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

export function isTimeValue(v: string | null | undefined): boolean {
  return typeof v === 'string' && TIME_RE.test(v);
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

export function toTimeValue(hours: number, minutes: number): TimeValue {
  return `${pad2(hours)}:${pad2(minutes)}`;
}

/** Minutes since midnight, or `null` when `v` is not a time. */
export function timeToMinutes(v: string | null | undefined): number | null {
  if (!v) return null;
  const m = TIME_RE.exec(v);
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
}

export function minutesToTime(total: number): TimeValue {
  const wrapped = ((total % 1440) + 1440) % 1440;
  return toTimeValue(Math.floor(wrapped / 60), wrapped % 60);
}

/**
 * Read a typed time leniently, because FREE TYPING IS THE POINT.
 *
 * The 15-minute list is a shortcut for the nine times out of ten a shift starts
 * on a quarter hour. It is not the vocabulary: a late-arrival threshold of
 * 07:07 and a prayer-break window of 12:38 both exist in this product, and a
 * control that could only express multiples of fifteen would send the reader
 * back to a text field somewhere else in the form.
 *
 * Accepted, all meaning the same thing: `7`, `07`, `730`, `7:3`, `7.30`,
 * `0730`, `07:30`. Rejected: anything past 23:59, which is not a clock time —
 * a shift that ends "at 24:00" ends at 00:00 the next day, and the DAY is a
 * different field.
 */
export function parseTime(text: string): TimeValue | null {
  const t = text.trim();
  if (!t) return null;
  const sep = /^(\d{1,2})\s*[:.\s]\s*(\d{1,2})$/.exec(t);
  if (sep) return clockOrNull(Number(sep[1]), Number(sep[2]));
  const digits = /^(\d{1,4})$/.exec(t);
  if (!digits) return null;
  const raw = digits[1] ?? '';
  // 1–2 digits is an hour on the hour; 3–4 is HMM/HHMM, the way a keypad is used.
  if (raw.length <= 2) return clockOrNull(Number(raw), 0);
  return clockOrNull(Number(raw.slice(0, raw.length - 2)), Number(raw.slice(-2)));
}

function clockOrNull(hours: number, minutes: number): TimeValue | null {
  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return null;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return toTimeValue(hours, minutes);
}

/** Every quarter hour from 00:00 to 23:45 — the list the popover scrolls. */
export function quarterHourSteps(stepMinutes = 15): TimeValue[] {
  const out: TimeValue[] = [];
  for (let m = 0; m < 1440; m += stepMinutes) out.push(minutesToTime(m));
  return out;
}

/**
 * The step a reader's own value sits on or nearest below, so the list opens
 * SHOWING where they already are. Scrolling a 96-row list to the top when the
 * field already reads 17:30 is the list refusing to answer the question it was
 * opened to answer.
 */
export function nearestStepAtOrBelow(value: string, steps: TimeValue[]): TimeValue | null {
  const mins = timeToMinutes(value);
  if (mins == null) return null;
  let best: TimeValue | null = null;
  for (const s of steps) {
    const sm = timeToMinutes(s);
    if (sm != null && sm <= mins) best = s;
  }
  return best;
}
